import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { logSignIn, logSignOut } from '../../services/activityLogService';
import { getSaasApiBaseUrl } from '../../utils/saasApiBaseUrl';
import { parseLoginError } from '@saas-foundation/shared-frontend/utils/authErrors';
import {
  persistAuthResponse,
  clearSessionTokens,
  clearAuthSession,
  startSessionKeepAlive,
  hydrateAuthFromUrl,
  fetchWithFreshAccess,
} from '@saas-foundation/shared-frontend/utils/sessionKeepAlive';

const getApiBaseUrl = getSaasApiBaseUrl;

// Security defaults (HIPAA-friendly): 15 min idle timeout, warn 60s before sign-out.
const INACTIVITY_TIMEOUT_MS = Number(process.env.REACT_APP_INACTIVITY_TIMEOUT_MS) || 15 * 60 * 1000;
const INACTIVITY_WARNING_MS = Number(process.env.REACT_APP_INACTIVITY_WARNING_MS) || 60 * 1000;

const AuthContext = createContext({});

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleCountdownSec, setIdleCountdownSec] = useState(Math.ceil(INACTIVITY_WARNING_MS / 1000));
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const warningDeadlineRef = useRef(0);
  const resetIdleTrackingRef = useRef(() => {});

  // Initialize authentication on app load
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setLoading(true);

      // Support launch from AgenticPractice in a new browser tab without separate login.
      // We hydrate local storage from query params once, then clean the URL.
      if (typeof window !== 'undefined') {
        hydrateAuthFromUrl();
        const params = new URLSearchParams(window.location.search);
        const incomingToken = params.get('auth_token') || params.get('token');
        const incomingOrgId = params.get('organization_id');
        if (incomingOrgId) {
          localStorage.setItem('selectedOrganizationId', incomingOrgId);
          localStorage.setItem('organizationId', incomingOrgId);
        }
        if (incomingToken || incomingOrgId) {
          const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }

      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token && !localStorage.getItem('refresh_token')) {
        setLoading(false);
        setInitialized(true);
        return;
      }

      // Check if user is authenticated (refresh expired access JWT first when needed)
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetchWithFreshAccess(API_BASE_URL, '/user/profile');

      if (response.ok) {
        const data = await response.json();
        const userData = data.user || data.data?.user || data;
        
        // Extract and store organization_id from user data
        const organizationId = userData?.organization_id || userData?.organizationId;
        if (organizationId) {
          localStorage.setItem('selectedOrganizationId', organizationId);
          localStorage.setItem('organizationId', organizationId);
        }
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        // Token invalid, clear it
        clearSessionTokens();
        localStorage.removeItem('user');
        localStorage.removeItem('selectedOrganizationId');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('organizationName');
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      clearSessionTokens();
      localStorage.removeItem('user');
      localStorage.removeItem('selectedOrganizationId');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('organizationName');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();
      
      if (response.ok) {
        // 2FA: user must enter verification code before we set token/user
        if (data.requires_mfa && data.mfa_token) {
          return {
            success: true,
            requiresMfa: true,
            mfaToken: data.mfa_token,
            mfaMethod: data.mfa_method || 'totp',
            message: data.message || 'Enter the 6-digit code from your authenticator app.',
          };
        }

        // Multi-org: user must select organization before we set token/user
        if (data.requires_organization_selection && data.organizations?.length > 0 && data.login_token) {
          return {
            success: true,
            requiresOrganizationSelection: true,
            organizations: data.organizations,
            loginToken: data.login_token,
          };
        }

        // API returns TokenResponse: { access_token, refresh_token, token_type, user }
        const userData = data.user || data;
        const token = persistAuthResponse(data);
        
        // Extract and store organization_id from user data
        const organizationId = userData?.organization_id || userData?.organizationId;
        if (organizationId) {
          localStorage.setItem('selectedOrganizationId', organizationId);
          localStorage.setItem('organizationId', organizationId);
        }
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Log sign-in event for HIPAA compliance
        if (organizationId && token) {
          logSignIn(userData, organizationId, token).catch(err => {
            // Only log sign-in logging failures in development
            if (process.env.NODE_ENV === 'development') {
              console.warn('Failed to log sign-in event:', err);
            }
          });
        }
        
        return { success: true, data: { user: userData, token, organizationId } };
      }

      if (response.status === 403) {
        const parsed = parseLoginError(data, credentials.email);
        if (parsed.requiresEmailVerification) {
          return {
            success: false,
            requiresEmailVerification: true,
            email: parsed.email,
            message: parsed.message,
          };
        }
        if (data?.detail) {
          const detail = data.detail;
          throw new Error(typeof detail === 'string' ? detail : detail.message || 'Login failed');
        }
      }

      // Always return generic error message for security (don't reveal if email exists)
      throw new Error('Invalid email or password');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', error);
      }
      throw new Error(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = async (loginToken, organizationId) => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/auth/complete-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          login_token: loginToken,
          organization_id: organizationId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Invalid or expired link. Please sign in again.');
      }

      const userData = data.user || data;
      const token = persistAuthResponse(data);
      const resolvedOrganizationId = organizationId || userData?.organization_id || userData?.organizationId;
      if (resolvedOrganizationId) {
        localStorage.setItem('selectedOrganizationId', resolvedOrganizationId);
        localStorage.setItem('organizationId', resolvedOrganizationId);
        userData.organization_id = resolvedOrganizationId;
        userData.organizationId = resolvedOrganizationId;
      }
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      if (resolvedOrganizationId && token) {
        logSignIn(userData, resolvedOrganizationId, token).catch(err => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to log sign-in event:', err);
          }
        });
      }

      return { success: true, data: { user: userData, token, organizationId: resolvedOrganizationId } };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Complete login error:', error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const mfaVerify = async (mfaToken, code) => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: mfaToken, code: code.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Invalid or expired code.');
      }

      if (data.requires_organization_selection && data.organizations?.length > 0 && data.login_token) {
        return {
          success: true,
          requiresOrganizationSelection: true,
          organizations: data.organizations,
          loginToken: data.login_token,
        };
      }

      const userData = data.user || data;
      const token = persistAuthResponse(data);
      const organizationId = userData?.organization_id || userData?.organizationId;
      if (organizationId) {
        localStorage.setItem('selectedOrganizationId', organizationId);
        localStorage.setItem('organizationId', organizationId);
      }
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      if (organizationId && token) {
        logSignIn(userData, organizationId, token).catch(err => {
          if (process.env.NODE_ENV === 'development') console.warn('Failed to log sign-in:', err);
        });
      }
      return { success: true, data: { user: userData, token, organizationId } };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('MFA verify error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resendMfa = async (mfaToken) => {
    const API_BASE_URL = getApiBaseUrl();
    const response = await fetch(`${API_BASE_URL}/auth/mfa/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfa_token: mfaToken }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || data.message || 'Could not resend code.');
    }
    return { success: true, mfaToken: data.mfa_token };
  };

  const mfaSetup = async (enable, method = 'totp') => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) throw new Error('Not authenticated');
    const API_BASE_URL = getApiBaseUrl();
    const response = await fetch(`${API_BASE_URL}/auth/mfa/setup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        enable: !!enable,
        method: enable ? (method || 'totp') : null,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detail = data.detail ?? data.message;
      throw new Error(typeof detail === 'string' ? detail : 'Failed to update 2FA.');
    }
    // A begin-enrollment response (data.enrollment) hasn't changed anything real
    // yet — mfa_enabled is still false and the QR/secret live in local component
    // state. Updating `user` here would churn its object identity and remount the
    // page (route guards refetch onboarding/RBAC/org on user change), discarding
    // the just-set QR enrollment. Only sync `user` on an actual state change
    // (enable-confirm handled in mfaConfirmSetup, or disable here).
    if (!data.enrollment) {
      setUser((prev) => {
        const updated = { ...(prev || {}), mfa_enabled: data.mfa_enabled, mfa_method: data.mfa_method };
        if (typeof localStorage !== 'undefined' && prev) {
          localStorage.setItem('user', JSON.stringify(updated));
        }
        return updated;
      });
    }
    return { success: true, data };
  };

  const mfaConfirmSetup = async (code) => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) throw new Error('Not authenticated');
    const API_BASE_URL = getApiBaseUrl();
    const response = await fetch(`${API_BASE_URL}/auth/mfa/setup/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ code: String(code || '').trim() }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detail = data.detail ?? data.message;
      throw new Error(typeof detail === 'string' ? detail : 'Invalid authenticator code.');
    }
    setUser((prev) => {
      const updated = { ...(prev || {}), mfa_enabled: data.mfa_enabled, mfa_method: data.mfa_method };
      if (typeof localStorage !== 'undefined' && prev) {
        localStorage.setItem('user', JSON.stringify(updated));
      }
      return updated;
    });
    return { success: true, data };
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json().catch(() => ({}));
      
      // Backend may return 200 with success: true, or with user_id (legacy) for successful registration
      const isSuccess = response.ok && (data.success === true || data.user_id);
      if (isSuccess) {
        // If registration includes auto-login
        if (data.data?.user) {
          const userData = data.data.user;
          const token = persistAuthResponse(data.data || data);
          
          // Extract and store organization_id from user data
          const organizationId = userData?.organization_id || userData?.organizationId;
          if (organizationId) {
            localStorage.setItem('selectedOrganizationId', organizationId);
            localStorage.setItem('organizationId', organizationId);
          }
          
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
        return { success: true, data };
      }
      
      // Backend returns 400/422 with { detail: "message" } or { detail: [{ msg, loc }] }
      const detail = data.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail) && detail.length > 0
          ? detail.map((e) => e.msg || e.message).filter(Boolean).join('; ') || data.message
          : data.message;
      throw new Error(message || 'Registration failed');
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const userData = user || JSON.parse(localStorage.getItem('user') || 'null');
      const organizationId = localStorage.getItem('selectedOrganizationId') || localStorage.getItem('organizationId');
      
      // Log sign-out event for HIPAA compliance (before clearing data)
      if (userData && organizationId && token) {
        logSignOut(userData, organizationId, token).catch(err => {
          console.warn('Failed to log sign-out event:', err);
        });
      }
      
      if (token) {
        const API_BASE_URL = getApiBaseUrl();
        try {
          await fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
        } catch (error) {
          console.error('Logout API error:', error);
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthSession();
      setShowIdleWarning(false);
      setUser(null);
      setLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const data = await response.json();
        const userData = data.user || data.data?.user || { ...user, ...profileData };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, data: userData };
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await fetch(`${API_BASE_URL}/user/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        return {
          success: true,
          message: data.message || 'Password changed successfully. A confirmation email has been sent.',
          email_sent: data.email_sent,
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail ?? errorData.message;
        const message = Array.isArray(detail)
          ? detail.map((d) => d.msg || d.message || String(d)).join(' ')
          : (typeof detail === 'string' ? detail : 'Failed to change password');
        throw new Error(message);
      }
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Not authenticated');
    }
    const API_BASE_URL = getApiBaseUrl();
    const response = await fetch(`${API_BASE_URL}/user/account`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const detail = data.detail;
      const message = typeof detail === 'string' ? detail : data.message || 'Failed to delete account';
      throw new Error(message);
    }
    clearSessionTokens();
    localStorage.removeItem('user');
    localStorage.removeItem('selectedOrganizationId');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('organizationName');
    setUser(null);
  };

  // Compute isAuthenticated as a boolean value (not a function)
  // This ensures it's reactive and can be used directly in React hooks
  // Use useMemo to make it reactive to user changes
  const isAuthenticated = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    return !!token && !!user;
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    return startSessionKeepAlive({
      getApiBaseUrl,
      onRefreshFailed: () => {
        clearSessionTokens();
        setUser(null);
      },
    });
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const hasSession = !!token && !!user;

    const clearTimers = () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      warningTimerRef.current = null;
      logoutTimerRef.current = null;
      countdownTimerRef.current = null;
    };

    const forceLogout = async () => {
      try {
        await logout();
      } finally {
        window.location.assign('/login?reason=timeout');
      }
    };

    const scheduleTimers = () => {
      clearTimers();
      setShowIdleWarning(false);
      setIdleCountdownSec(Math.ceil(INACTIVITY_WARNING_MS / 1000));

      const warningDelay = Math.max(0, INACTIVITY_TIMEOUT_MS - INACTIVITY_WARNING_MS);
      warningTimerRef.current = setTimeout(() => {
        warningDeadlineRef.current = Date.now() + INACTIVITY_WARNING_MS;
        setShowIdleWarning(true);
        setIdleCountdownSec(Math.ceil(INACTIVITY_WARNING_MS / 1000));

        countdownTimerRef.current = setInterval(() => {
          const msLeft = Math.max(0, warningDeadlineRef.current - Date.now());
          setIdleCountdownSec(Math.ceil(msLeft / 1000));
        }, 1000);

        logoutTimerRef.current = setTimeout(() => {
          forceLogout();
        }, INACTIVITY_WARNING_MS);
      }, warningDelay);
    };

    resetIdleTrackingRef.current = scheduleTimers;

    if (!hasSession) {
      clearTimers();
      setShowIdleWarning(false);
      return clearTimers;
    }

    const onActivity = () => scheduleTimers();
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
    scheduleTimers();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, onActivity));
      clearTimers();
    };
  }, [user]);

  const value = {
    user,
    loading,
    initialized,
    login,
    completeLogin,
    mfaVerify,
    resendMfa,
    mfaSetup,
    mfaConfirmSetup,
    register,
    logout,
    updateUser,
    updateProfile,
    changePassword,
    deleteAccount,
    isAuthenticated, // Now a boolean, not a function
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showIdleWarning ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-amber-200 bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Session expiring soon</h3>
            <p className="mt-2 text-sm text-gray-600">
              For security, you will be signed out due to inactivity in about{' '}
              <span className="font-semibold text-gray-900">{idleCountdownSec}s</span>.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={logout}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Sign out now
              </button>
              <button
                type="button"
                onClick={() => resetIdleTrackingRef.current()}
                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AuthContext.Provider>
  );
};

